import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { API } from 'aws-amplify';
import { ConfigService } from '../../config';

export interface StripeReaderAssignment {
	machine_id: string;
	machine_name: string;
	disti_id: number | null;
	market_id: number | null;
	client_id: number | null;
	client_name: string;
	campus_id: number | null;
	campus_name: string;
	// The Stripe location the machine's campus is linked to ('' when not linked)
	campus_stripe_location_id?: string;
	assigned_at: string | null;
}

export interface StripeReader {
	stripe_reader_id: string;
	label: string;
	serial_number: string;
	status: string;
	device_type: string;
	location_id: string;
	// false when an assignment points at a reader Stripe no longer returns
	in_stripe?: boolean;
	assignment: StripeReaderAssignment | null;
}

export interface CampusAddress {
	line1: string;
	line2: string;
	city: string;
	state: string;
	postal_code: string;
	country: string;
}

export interface MachineCampus {
	campus_id: number;
	name: string;
	client_name: string;
	address: CampusAddress;
}

// The campus a reader registered here goes to; the user confirms it before registering.
export interface MachineCampusResult {
	machine_id: string;
	campus: MachineCampus | null;
	// false when Stripe can't make a location from the campus address
	address_complete: boolean;
}

export interface DeregisterStripeReaderResult {
	stripe_reader_id: string;
	assignment_removed: boolean;
	deleted_in_stripe: boolean;
}

export interface ListStripeReadersResult {
	readers: StripeReader[];
	stripe_total: number;
}

export interface AssignStripeReaderPayload {
	machine_id: string;
}

// The reader is registered to the Stripe location of the machine's campus, which is created if
// needed, and named "<machine nickname> - <machine id>". confirmed_campus_id is the campus the
// user confirmed; the server refuses if it changed.
export interface RegisterStripeReaderPayload {
	registration_code: string;
	machine_id: string;
	confirmed_campus_id: number;
}

export interface AssignStripeReaderResult {
	stripe_reader_id: string;
	label?: string;
	serial_number?: string;
	status?: string;
	device_type?: string;
	location_id?: string;
	assignment: StripeReaderAssignment;
	iot?: {
		thing_name: string;
		status: string;
	};
}

export interface StripeReadersApiResponse<T> {
	status: number;
	message: string;
	data: T;
	error?: unknown;
}

@Injectable({
	providedIn: 'root'
})
export class StripeReadersHttpService {
	private apiName: string;

	constructor(
		private configService: ConfigService,
		private ngZone: NgZone
	) {
		this.apiName = this.configService.get('host');
	}

	listReaders(): Observable<StripeReadersApiResponse<ListStripeReadersResult>> {
		return this.fromAmplify(API.get(this.apiName, '/dashboard/stripe/readers', { response: false }));
	}

	registerReader(
		payload: RegisterStripeReaderPayload
	): Observable<StripeReadersApiResponse<AssignStripeReaderResult>> {
		return this.fromAmplify(
			API.post(this.apiName, '/dashboard/stripe/readers/register', {
				response: false,
				body: payload
			})
		);
	}

	assignReader(
		stripeReaderId: string,
		payload: AssignStripeReaderPayload
	): Observable<StripeReadersApiResponse<AssignStripeReaderResult>> {
		return this.fromAmplify(
			API.put(this.apiName, `/dashboard/stripe/readers/${encodeURIComponent(stripeReaderId)}`, {
				response: false,
				body: payload
			})
		);
	}

	deregisterReader(
		stripeReaderId: string
	): Observable<StripeReadersApiResponse<DeregisterStripeReaderResult>> {
		return this.fromAmplify(
			API.del(this.apiName, `/dashboard/stripe/readers/${encodeURIComponent(stripeReaderId)}/registration`, {
				response: false
			})
		);
	}

	getMachineCampus(machineId: string): Observable<StripeReadersApiResponse<MachineCampusResult>> {
		return this.fromAmplify(
			API.get(this.apiName, `/dashboard/stripe/machines/${encodeURIComponent(machineId)}/campus`, {
				response: false
			})
		);
	}

	private fromAmplify<T>(promise: Promise<T>): Observable<T> {
		return new Observable(observer => {
			let cancelled = false;
			promise
				.then(response => {
					if (cancelled) {
						return;
					}
					this.ngZone.run(() => {
						observer.next(response);
						observer.complete();
					});
				})
				.catch(error => {
					if (cancelled) {
						return;
					}
					this.ngZone.run(() => observer.error(error));
				});
			return () => {
				cancelled = true;
			};
		});
	}
}
